import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Search, Truck, Route, Users, X } from "lucide-react";

const GlobalSearch = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ clients: [], livraisons: [], tournees: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  // Cmd+K / Ctrl+K to open
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQuery(""); setResults({ clients: [], livraisons: [], tournees: [] }); }
  }, [open]);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults({ clients: [], livraisons: [], tournees: [] }); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`, { headers });
      setResults(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => search(query), 250);
    return () => clearTimeout(t);
  }, [query, search]);

  const go = (path) => { navigate(path); setOpen(false); };

  const total = results.clients.length + results.livraisons.length + results.tournees.length;

  if (!open) return (
    <button
      onClick={() => setOpen(true)}
      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-400 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
      data-testid="global-search-btn"
    >
      <Search className="w-4 h-4" />
      <span className="flex-1 text-left">Rechercher...</span>
      <kbd className="text-xs bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-400">⌘K</kbd>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4" onClick={() => setOpen(false)}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Chercher un client, livraison, tournée..."
            className="flex-1 text-sm outline-none placeholder-slate-400"
            data-testid="global-search-input"
          />
          {loading && <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />}
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {query.length >= 2 && total === 0 && !loading && (
            <p className="text-sm text-slate-400 text-center py-8">Aucun résultat pour "{query}"</p>
          )}

          {results.clients.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase px-4 pt-3 pb-1">Clients</p>
              {results.clients.map(c => (
                <button key={c.id} onClick={() => go("/clients")} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left">
                  <Users className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{c.nom}</p>
                    <p className="text-xs text-slate-400">{c.telephone} · {c.ville}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results.livraisons.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase px-4 pt-3 pb-1">Livraisons</p>
              {results.livraisons.map(l => (
                <button key={l.id} onClick={() => go("/livraisons")} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left">
                  <Truck className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{l.client_nom}</p>
                    <p className="text-xs text-slate-400">{l.adresse}, {l.ville} · {l.statut}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results.tournees.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase px-4 pt-3 pb-1">Tournées</p>
              {results.tournees.map(t => (
                <button key={t.id} onClick={() => go("/tournees")} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left">
                  <Route className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t.nom}</p>
                    <p className="text-xs text-slate-400">{t.date} · {t.statut}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {query.length < 2 && (
            <p className="text-xs text-slate-400 text-center py-6">Tapez au moins 2 caractères pour rechercher</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
